import { useCallback, useEffect, useState } from 'react';

/**
 * Frontend-driven cooldown timer for resend-code flows.
 *
 * Apr-19 fix per Apr-18 Issue 1: the prior countdown
 * relied on `localStorage.getItem('email-code-expiry')` →
 * `new Date(expiry)` → `getRemainingTimeInSeconds(expiry)`. Backend masks
 * `expires_at: null` (Apr-18 security hardening), so the
 * stored value becomes the literal string `"null"`, which parses to
 * Invalid Date, which cascades to `NaN` on the countdown state — user
 * saw *"Resend code: NaNs"* on the email-verification screen.
 *
 * This hook drops the backend dependency entirely. Cooldown lives purely
 * in React state, default 60 seconds. Matches the backend per-IP throttle
 * (5 req 60s from Apr-18) so the UI never invites a
 * request the backend would reject.
 *
 * Usage:
 *   const { secondsLeft, isOnCooldown, startCooldown } = useResendCooldown();
 *   // on mount if a code was just sent, or after a successful resend:
 *   useEffect(() => startCooldown(), []);
 *   // render:
 *   {isOnCooldown ? `Resend in ${secondsLeft}s` : <ResendButton />}
 */
export function useResendCooldown(durationSeconds = 60) {
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = setInterval(() => {
      setSecondsLeft((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  const startCooldown = useCallback(() => {
    setSecondsLeft(durationSeconds);
  }, [durationSeconds]);

  return {
    secondsLeft,
    isOnCooldown: secondsLeft > 0,
    startCooldown,
  };
}
